// src/features/passwords/components/ExpandedRows.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ChildRow from './ChildRows';
import SearchableSelect from '../../user-dashboard/components/SearchableSelect';
import { copyToClipboard } from '../utils/copyToClipboard';

export default function ExpandedRows({
  userCode,
  userPasswords,
  withMultilogin = false,
  autoExpandAll = false,

  // forwarded props
  users,
  ports,
  onStartEditingRow,
  editingId,
  editDraft,
  onChangeEditField,
  onCancelEditingRow,
  onSaveEditingRow,
  onSaveAndApplyAll,

  showAddressEditor,
  setShowAddressEditor,
  addressParts,
  setAddressParts,
  parseAddressIntoParts,
  handleAddressSave,

  addingNewRecord,
  setAddingNewRecord,
  newRecordUserCode,
  setNewRecordUserCode,
  newRecordDraft,
  setNewRecordDraft,
  onCreateNewRecord,
  onDeleteRecord,

  onActivatePort,
  getStatusBadge,  // ✅ already here
  
  // sites for dropdown
  sites = [],
  isStaff = false,
  onRequestSite = null,
  // inline multilogin
  onToggleMultilogin,
  multiloginOverrides = {},
  onToggleMlEnvironment,
  mlEnvironmentOverrides = {},
  // persona check / self transfer
  showPersonaSelfTransfer = false,
  onPersonaCheckSubmit = null,
  onPersonaCheckClear = null,
  onSelfTransferToggle = null,
  personaSelfOverrides = {},
}) {
  const [expandedEmails, setExpandedEmails] = useState({});

  const mergeEntry = (entry) => {
    const ov = personaSelfOverrides[entry.id];
    return ov ? { ...entry, ...ov } : entry;
  };

  const childColSpan =
    (withMultilogin ? 11 : 10) + (showPersonaSelfTransfer ? 2 : 0);

  // Group passwords by email (normalize key so same email with different casing/whitespace stays one group)
  const emailGroups = useMemo(() => {
    const groups = {};
    userPasswords.forEach((entry) => {
      const raw = (entry.email != null && entry.email !== '') ? String(entry.email).trim() : '';
      const emailKey = raw ? raw.toLowerCase() : 'no-email';
      if (!groups[emailKey]) {
        groups[emailKey] = [];
      }
      groups[emailKey].push(entry);
    });
    return groups;
  }, [userPasswords]);

  // When autoExpandAll is true (e.g. after bulk create or single-user search), expand all email groups
  useEffect(() => {
    if (autoExpandAll && Object.keys(emailGroups).length > 0) {
      const allExpanded = {};
      Object.keys(emailGroups).forEach((emailKey) => {
        allExpanded[emailKey] = true;
      });
      setExpandedEmails(allExpanded);
    }
  }, [autoExpandAll, emailGroups]);

  const toggleEmailExpand = (email) => {
    setExpandedEmails(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };

  return (
    <>
      {Object.entries(emailGroups).map(([emailKey, entries]) => {
        const firstEntry = entries[0];
        const displayEmail = (firstEntry.email != null && firstEntry.email !== '') ? String(firstEntry.email).trim() : emailKey;
        // Default to expanded (true) so multiple rows per email (e.g. test@test.com) are visible without clicking
        const isExpanded = expandedEmails[emailKey] !== false;
        const hasMultiple = entries.length > 1;

        return (
          <React.Fragment key={`${userCode}-${emailKey}-${firstEntry.id}`}>
            {/* First entry always visible */}
            <ChildRow
              key={firstEntry.id}
              userCode={userCode}
              entry={mergeEntry(firstEntry)}
              isEditing={editingId === firstEntry.id}
              editDraft={editDraft}
              onChange={onChangeEditField}
              onCancel={onCancelEditingRow}
              onSave={onSaveEditingRow}
              onSaveApplyAll={onSaveAndApplyAll}
              onEdit={(e) => onStartEditingRow?.(e ?? firstEntry)}
              showAddressEditor={showAddressEditor}
              setShowAddressEditor={setShowAddressEditor}
              addressParts={addressParts}
              setAddressParts={setAddressParts}
              onAddressSave={handleAddressSave}
              onCopy={(text) => copyToClipboard(text)}
              onActivatePort={onActivatePort}
              onDelete={() => onDeleteRecord?.(firstEntry)}
              showMultilogin={withMultilogin}
              multiloginFlag={
                firstEntry.id in multiloginOverrides
                  ? multiloginOverrides[firstEntry.id]
                  : Boolean(firstEntry.multilogin ?? users?.[userCode]?.base?.multilogin)
              }
              multiloginSaving={multiloginOverrides[firstEntry.id + '_saving'] === true}
              onToggleMultilogin={(val) => onToggleMultilogin?.(firstEntry, val)}
              mlEnvironment={
                firstEntry.id in mlEnvironmentOverrides
                  ? mlEnvironmentOverrides[firstEntry.id]
                  : (firstEntry.email_specific_ml_environment || null)
              }
              onToggleMlEnvironment={(val) => onToggleMlEnvironment?.(firstEntry, val)}
              getStatusBadge={getStatusBadge}
              ports={ports}  // ✅ PASS ports
              sites={sites}
              isStaff={isStaff}
              onRequestSite={onRequestSite}
              showPersonaSelfTransfer={showPersonaSelfTransfer}
              onPersonaCheckSubmit={onPersonaCheckSubmit}
              onPersonaCheckClear={onPersonaCheckClear}
              onSelfTransferToggle={onSelfTransferToggle}
            />

            {/* Show expand/collapse button if multiple entries for this email */}
            {hasMultiple && (
              <tr>
                <td colSpan={childColSpan} style={{ padding: '4px 12px' }}>
                  <button
                    className="btn btn-link btn-sm p-0 text-muted"
                    onClick={() => toggleEmailExpand(emailKey)}
                    style={{ fontSize: '12px', textDecoration: 'none' }}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronDown size={14} className="me-1" />
                        Hide {entries.length - 1} more {entries.length - 1 === 1 ? 'entry' : 'entries'} for {displayEmail}
                      </>
                    ) : (
                      <>
                        <ChevronRight size={14} className="me-1" />
                        Show {entries.length - 1} more {entries.length - 1 === 1 ? 'entry' : 'entries'} for {displayEmail}
                      </>
                    )}
                  </button>
                </td>
              </tr>
            )}

            {/* Additional entries (hidden by default) */}
            {hasMultiple && isExpanded && entries.slice(1).map((entry) => (
              <ChildRow
                key={entry.id}
                userCode={userCode}
                entry={mergeEntry(entry)}
                isEditing={editingId === entry.id}
                editDraft={editDraft}
                onChange={onChangeEditField}
                onCancel={onCancelEditingRow}
                onSave={onSaveEditingRow}
                onSaveApplyAll={onSaveAndApplyAll}
                onEdit={(e) => onStartEditingRow?.(e ?? entry)}
                showAddressEditor={showAddressEditor}
                setShowAddressEditor={setShowAddressEditor}
                addressParts={addressParts}
                setAddressParts={setAddressParts}
                onAddressSave={handleAddressSave}
                onCopy={(text) => copyToClipboard(text)}
                onActivatePort={onActivatePort}
                onDelete={() => onDeleteRecord?.(entry)}
                showMultilogin={withMultilogin}
                multiloginFlag={
                  entry.id in multiloginOverrides
                    ? multiloginOverrides[entry.id]
                    : Boolean(entry.multilogin ?? users?.[userCode]?.base?.multilogin)
                }
                multiloginSaving={multiloginOverrides[entry.id + '_saving'] === true}
                onToggleMultilogin={(val) => onToggleMultilogin?.(entry, val)}
                mlEnvironment={
                  entry.id in mlEnvironmentOverrides
                    ? mlEnvironmentOverrides[entry.id]
                    : (entry.email_specific_ml_environment || null)
                }
                onToggleMlEnvironment={(val) => onToggleMlEnvironment?.(entry, val)}
                isExpandedRow={true}
                getStatusBadge={getStatusBadge}
                ports={ports}  // ✅ PASS ports
                sites={sites}
                isStaff={isStaff}
                onRequestSite={onRequestSite}
                showPersonaSelfTransfer={showPersonaSelfTransfer}
                onPersonaCheckSubmit={onPersonaCheckSubmit}
                onPersonaCheckClear={onPersonaCheckClear}
                onSelfTransferToggle={onSelfTransferToggle}
              />
            ))}
          </React.Fragment>
        );
      })}

      {/* Create new child row for this user */}
      {addingNewRecord && newRecordUserCode === userCode ? (
        <tr className="align-middle table-warning">
          <td>{userCode}</td>
          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Full name (email-specific)"
              value={newRecordDraft.fullName}
              onChange={(e) => setNewRecordDraft((p) => ({ ...p, fullName: e.target.value }))}
            />
          </td>
          {withMultilogin && <td>{/* Multilogin is user-level only; read-only here */}</td>}
          <td>
            <input
              type="email"
              className="form-control form-control-sm"
              placeholder="Email"
              value={newRecordDraft.email}
              onChange={(e) => setNewRecordDraft((p) => ({ ...p, email: e.target.value }))}
            />
          </td>
          <td />
          <td>
            <SearchableSelect
              value={newRecordDraft.site}
              onChange={(value) => setNewRecordDraft((p) => ({ ...p, site: value }))}
              options={sites.map(site => ({ value: site, label: site }))}
              placeholder="Site"
              allowCustom={isStaff}
              className="form-select form-select-sm"
            />
          </td>
          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Password"
              value={newRecordDraft.password}
              onChange={(e) => setNewRecordDraft((p) => ({ ...p, password: e.target.value }))}
            />
          </td>
          <td />
          {showPersonaSelfTransfer && (
            <>
              <td />
              <td />
            </>
          )}
          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Address"
              value={newRecordDraft.address}
              onChange={(e) => setNewRecordDraft((p) => ({ ...p, address: e.target.value }))}
            />
          </td>
          <td>
            <input
              type="date"
              className="form-control form-control-sm"
              value={newRecordDraft.dob || ''}
              onChange={(e) => setNewRecordDraft((p) => ({ ...p, dob: e.target.value }))}
            />
          </td>
          <td>
            <button
              className="btn btn-primary btn-sm me-1"
              onClick={() => onCreateNewRecord?.(userCode)}
            >
              Create
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setAddingNewRecord(false);
                setNewRecordUserCode(null);
              }}
            >
              Cancel
            </button>
          </td>
        </tr>
      ) : (
        <tr>
          <td colSpan={childColSpan}>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => {
                setAddingNewRecord(true);
                setNewRecordUserCode(userCode);
                setNewRecordDraft({
                  email: '',
                  site: '',
                  password: '',
                  address: '',
                  dob: '',
                  fullName: '',
                });
              }}
            >
              + Add row for {userCode}
            </button>
          </td>
        </tr>
      )}
    </>
  );
}